import type { PreparationContent } from "./preparation-studio.js";

// Onboarding predates structured preparation and rejects unknown keys. Keep
// this projection explicit when preparation acquires another optional field.
export function onboardingAnswers(content: PreparationContent, initialContacts: string) {
  return {
    companyName: content.companyName,
    businessArea: content.businessArea,
    services: content.services,
    commercialInfo: content.commercialInfo,
    businessHours: content.businessHours,
    businessObjectives: content.businessObjectives,
    attendanceRules: content.attendanceRules,
    humanHandoff: content.humanHandoff,
    faqs: content.faqs.filter((item) => item.question && item.answer),
    aiInstructions: content.aiInstructions,
    nicheAnswer: "",
    allowedContacts: initialContacts.split(",").map((item) => item.trim()).filter(Boolean)
  };
}

function has(value: unknown) {
  return typeof value === "string" ? Boolean(value.trim()) : Array.isArray(value) ? value.length > 0 : Boolean(value);
}
const nameKey = (name: string) => name.trim().toLocaleLowerCase("pt-BR");

export function mergeReviewedSiteImport(
  current: PreparationContent,
  proposal: Partial<PreparationContent>,
  fields: Array<keyof PreparationContent>,
  selectedOfferIds: string[],
  replaceSiteOffers: boolean,
  preserveExisting = false
): PreparationContent {
  const next = { ...current };
  for (const field of fields.filter((key) => key !== "knowledge")) {
    const value = proposal[field];
    if (value !== undefined && has(value) && (!preserveExisting || !has(current[field]))) Object.assign(next, { [field]: value });
  }
  const siteKnowledge = proposal.knowledge;
  const extra = fields.includes("knowledge") && siteKnowledge
    ? Object.fromEntries(Object.entries(siteKnowledge).filter(([key, value]) => key !== "offers" && has(value) && (!preserveExisting || !has(current.knowledge[key as keyof typeof current.knowledge]))))
    : {};
  const oldSiteNames = new Set(current.knowledge.offers.filter((item) => replaceSiteOffers && item.source === "site").map((item) => nameKey(item.name)));
  const retained = current.knowledge.offers.filter((item) => !replaceSiteOffers || item.source !== "site");
  const seen = new Set(retained.map((item) => nameKey(item.name)));
  const imported = (siteKnowledge?.offers ?? []).filter((item) => selectedOfferIds.includes(item.id)).flatMap((item) => {
    const key = nameKey(item.name);
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ ...item, reviewed: true }];
  });
  next.knowledge = { ...current.knowledge, ...extra, offers: [...retained, ...imported] };
  next.services = next.services.filter(name => !oldSiteNames.has(nameKey(name)));
  if (fields.includes("services")) {
    const structured = new Set(next.knowledge.offers.map(item => nameKey(item.name)));
    next.services = [...new Set([...current.services.filter(name => !oldSiteNames.has(nameKey(name))), ...next.services.filter(name => !structured.has(nameKey(name)))])];
  }
  return next;
}
